import { Component, AfterViewInit, ViewChild, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, Validators, FormControl } from '@angular/forms';
import { GoogleMap, MapPolygon } from '@angular/google-maps';
import { RouterModule, Router } from '@angular/router';

// PrimeNG Components
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { Carousel, CarouselModule } from 'primeng/carousel';

// Services and Models
import { TerritoryService } from '../../../core/services/territory.service';
import { AuthService } from '../../../core/services/auth';
import { UserService } from '../../../core/services/user.service';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { PermissionService } from '../../../core/services/permission.service';
import { TerritoryWithDetails } from '../../../core/models/territory.model';
import { SETTINGS } from '../../../core/config/common.settings';

// PrimeNG Services
import { MessageService } from 'primeng/api';
interface Subscriber {
    _id: string;
    name: string;
    email: string;
    status: string;
    subscriptionId?: string;
}

interface Subscription {
    id: string;
    name: string;
    status: string;
    email: string;
}

@Component({
    selector: 'app-territory-management',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        GoogleMap,
        MapPolygon,
        ButtonModule,
        SelectModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        ProgressSpinnerModule,
        TagModule,
        ToastModule,
        ConfirmDialogModule,
        RouterModule,
        CarouselModule
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './territory-management.html',
    styleUrl: './territory-management.scss'
})
export class TerritoryManagementComponent implements AfterViewInit, OnDestroy {
    @ViewChild('mapRef', { static: false }) mapRef!: GoogleMap;
    @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
    @ViewChild('territoryCarousel', { static: false }) territoryCarousel!: Carousel;

    // Data
    territories: TerritoryWithDetails[] = [];
    selectedTerritory: TerritoryWithDetails | null = null;
    loading = false;
    searchQuery = '';
    regionSelected = false;
    selectedRegionObject: any = null; // Full region object

    // Carousel properties
    responsiveOptions: any[] = [];
    products: any[] = [];
    private previousSearchQuery = '';
    carouselKey = 0; // Key to force carousel reset
    // Session storage key for remembering last selected region
    private readonly SESSION_STORAGE_KEY = 'territory_management_last_region';
    
    // Wheel event listener for Ctrl+Scroll zoom
    private wheelListener?: () => void;
    private mouseEnterListener?: () => void;
    private mouseLeaveListener?: () => void;
    private parentScrollable?: HTMLElement | null;

    // Map properties
    center: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
    zoom = 4;
    options: google.maps.MapOptions = {
        mapTypeId: 'roadmap' as google.maps.MapTypeId,
        zoomControl: true,
        scrollwheel: false, // Disabled to handle Ctrl+Scroll manually
        disableDoubleClickZoom: true,
        maxZoom: 20,
        minZoom: 3,
    };

    // Forms
    territoryForm: FormGroup;

    // Filters
    filters: any = {}; // Assuming TerritoryFilters is no longer needed or replaced
    regions: any[] = [];
    allRegions = SETTINGS.REGIONS; // Changed to use SETTINGS.REGIONS

    constructor(
        private territoryService: TerritoryService,
        private authService: AuthService,
        private userService: UserService,
        private subscriberService: SubscriberService,
        private subscriptionContextService: SubscriptionContextService,
        private permissionService: PermissionService,
        private messageService: MessageService,
        private router: Router,
        private ngZone: NgZone
    ) {
        this.territoryForm = new FormGroup({
            name: new FormControl('', Validators.required),
            region: new FormControl('', Validators.required),
            status: new FormControl('Active')
        });

        this.responsiveOptions = [
            {
                breakpoint: '1400px',
                numVisible: 3,
                numScroll: 1
            },
            {
                breakpoint: '1199px',
                numVisible: 3,
                numScroll: 1
            },
            {
                breakpoint: '769px',
                numVisible: 2.2,
                numScroll: 1
            },
            {
                breakpoint: '575px',
                numVisible: 1,
                numScroll: 1
            }
        ]
    }

    ngAfterViewInit(): void {
        this.initializeMap();
        this.loadSubscriberOperationalRegions();
    }

    ngOnDestroy(): void {
        this.removeWheelListener();
        this.removeMouseListeners();
    }

    private async refreshCurrentUserData(): Promise<any> {
        try {
            const response = await this.userService.getCurrentUserProfile().toPromise();
            console.log('Refreshed user data from API (with territories):', response);
            
            // Update the stored user data with fresh data that includes territories
            if (response) {
                this.authService.setLoggedInUser(JSON.stringify(response));
                return response;
            }
            return null;
        } catch (error) {
            console.error('Failed to refresh user data:', error);
            return null;
        }
    }

    private loadLastSelectedRegion(): void {
        // Try to load the last selected region from session storage
        const lastRegionCode = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
        
        if (lastRegionCode && this.regions.length > 0) {
            // Find the region by code
            const lastRegion = this.regions.find(region => region.code === lastRegionCode);
            if (lastRegion) {
                this.selectedRegionObject = lastRegion;
                this.regionSelected = true;
                this.applyRegionToMap();
                this.loadTerritoriesByRegion();
                console.log('Loaded last selected region from session:', lastRegion.name);
                return;
            }
        }
        
        // If no last region or region not found, load the first region by default
        if (this.regions.length > 0) {
            this.selectedRegionObject = this.regions[0];
            this.regionSelected = true;
            this.applyRegionToMap();
            this.loadTerritoriesByRegion();
            console.log('Loaded first region by default:', this.regions[0].name);
        }
    }

    private loadSubscriberOperationalRegions(): void {
        // Get the selected subscriber ID from the subscription context service (used by interceptor)
        const selectedSubscriberId = this.subscriptionContextService.getSelectedSubscriberId();
        
        if (selectedSubscriberId) {
            console.log('Loading operational regions for selected subscriber:', selectedSubscriberId);
            this.subscriberService.findSubscriber(selectedSubscriberId).then((subscriber: any) => {
                // Check if subscriber is wrapped in a response object
                let actualSubscriber = subscriber;
                if (subscriber && subscriber.subscriber) {
                    actualSubscriber = subscriber.subscriber;
                }
                
                if (actualSubscriber?.operationalRegions && actualSubscriber.operationalRegions.length > 0) {
                    // Filter regions to only show those in subscriber's operational regions
                    this.regions = this.allRegions.filter(region => 
                        actualSubscriber.operationalRegions.includes(region.name)
                    );
                    console.log('Filtered regions based on subscriber operational regions:', this.regions);
                    console.log('Subscriber operational regions:', actualSubscriber.operationalRegions);
                } else {
                    // If no operational regions set, show all regions
                    this.regions = [...this.allRegions];
                    console.log('No operational regions found, showing all regions');
                }
                
                // After regions are loaded, select the first region by default
                this.loadLastSelectedRegion();
            }).catch((error: any) => {
                console.error('Error loading subscriber operational regions:', error);
                // Fallback to showing all regions
                this.regions = [...this.allRegions];
                // After regions are loaded, select the first region by default
                this.loadLastSelectedRegion();
            });
        } else {
            // If no selected subscriber ID, show all regions
            this.regions = [...this.allRegions];
            console.log('No selected subscriber ID found, showing all regions');
            // After regions are loaded, select the first region by default
            this.loadLastSelectedRegion();
        }
    }

    private saveLastSelectedRegion(): void {
        if (this.selectedRegionObject?.code) {
            sessionStorage.setItem(this.SESSION_STORAGE_KEY, this.selectedRegionObject.code);
            console.log('Saved region to session storage:', this.selectedRegionObject.code);
        }
    }

    private initializeMap(): void {
        if (this.mapRef?.googleMap) {
            // Map is ready
            console.log('Map initialized successfully');
            this.setupWheelZoomListener();
        } else {
            setTimeout(() => this.initializeMap(), 100);
        }
    }

    onMapReady(): void {
        console.log('Map is ready');
        // If we have a selected region, apply it to the map
        if (this.selectedRegionObject) {
            this.applyRegionToMap();
        }
    }

    private applyRegionToMap(): void {
        if (this.selectedRegionObject && this.mapRef?.googleMap) {
            // Update map center and zoom to the selected region
            this.center = this.selectedRegionObject.center;
            this.zoom = this.selectedRegionObject.zoom;
            
            // Update map options to restrict bounds to the region
            this.options = {
                ...this.options,
                restriction: {
                    latLngBounds: this.selectedRegionObject.bounds,
                    strictBounds: false
                }
            };
            
            console.log('Applied region to map:', this.selectedRegionObject.name);
        }
    }

    onRegionSelect(): void {
        if (this.selectedRegionObject) {
            // Apply the region to the map
            this.applyRegionToMap();
            
            this.regionSelected = true;
            this.loadTerritoriesByRegion();
            
            // Save the selected region to session storage
            this.saveLastSelectedRegion();
            
            // Log the region selection for debugging
            console.log('Selected region object:', this.selectedRegionObject);
            console.log('Map center:', this.center);
            console.log('Map zoom:', this.zoom);
        } else {
            console.error('Region object not found for selection');
        }
    }

    showFullRegion(): void {
        if (this.selectedRegionObject && this.mapRef?.googleMap) {
            // Fit the map bounds to show the full region area
            const bounds = new google.maps.LatLngBounds(
                { lat: this.selectedRegionObject.bounds.south, lng: this.selectedRegionObject.bounds.west },
                { lat: this.selectedRegionObject.bounds.north, lng: this.selectedRegionObject.bounds.east }
            );
            
            this.mapRef.googleMap.fitBounds(bounds);
            console.log('Showing full region area:', this.selectedRegionObject.name);
        }
    }

    async loadTerritoriesByRegion(): Promise<void> {
        this.loading = true;
        
        // Get current user's subscriber ID and role
        let currentUser = this.authService.getLoggedInUser();
        const userRole = currentUser?.role?.identification || currentUser?.roleId?.identification;
        
        console.log('Initial user object:', currentUser);
        console.log('User territories from localStorage:', currentUser?.territories);
        console.log('User role:', userRole);
        
        // If user is FSR, refresh user data to ensure territories are populated
        if (userRole === 'FSR') {
            console.log('FSR user detected, refreshing user data to get territories...');
            const refreshedUser = await this.refreshCurrentUserData();
            if (refreshedUser) {
                currentUser = refreshedUser;
                console.log('Refreshed user data:', currentUser);
                console.log('Refreshed user territories:', currentUser?.territories);
            }
        }
        
        const subscriberId = currentUser?.subscriberId;
        console.log('Using subscriber ID:', subscriberId);
     
        // Get the region name from the selected region object
        const regionName = this.selectedRegionObject?.name;

        // Load territories filtered by region and subscriber ID
        this.territoryService.getTerritoriesWithUserDetails().subscribe({
            next: (response) => {
                console.log('All territories from backend:', response.data);
                console.log('Current user subscriber ID:', currentUser.subscriberId);
                console.log('Selected region:', regionName);
                
                // Filter territories by selected region and subscriber ID
                let filteredTerritories = response.data.filter(territory => {
                    const regionMatch = territory.region === regionName;
                    
                    console.log(`Territory ${territory.name}: region=${territory.region}, subscriberId=${territory.subscriberId}`);
                    
                    return regionMatch;
                });

                // If user is FSR, further filter to show only territories they are assigned to
                if (userRole === 'FSR') {
                    const userTerritories = currentUser?.territories || [];
                    console.log('=== FSR TERRITORY FILTERING DEBUG ===');
                    console.log('FSR user assigned territories (raw):', userTerritories);
                    console.log('Type of territories:', typeof userTerritories);
                    console.log('Is territories array?', Array.isArray(userTerritories));
                    console.log('Territories length:', userTerritories.length);
                    
                    // Extract territory IDs using helper method
                    const userTerritoryIds = this.extractTerritoryIds(userTerritories);
                    console.log('Extracted territory IDs:', userTerritoryIds);
                    console.log('Number of extracted IDs:', userTerritoryIds.length);
                    
                    console.log('Available territories in this region:');
                    filteredTerritories.forEach(t => console.log(`- ${t.name} (${t._id})`));
                    
                    filteredTerritories = filteredTerritories.filter(territory => {
                        const isAssigned = this.isTerritoryAssignedToCurrentUser(territory._id);
                        console.log(`Territory ${territory.name} (${territory._id}) assigned to FSR:`, isAssigned);
                        return isAssigned;
                    });
                    
                    console.log('=== END FSR FILTERING DEBUG ===');
                }
                
                this.territories = filteredTerritories;
                console.log('Filtered territories:', this.territories);
                
                // Update products array for mobile carousel
                this.getFilteredTerritories();
                
                this.loading = false;
                
                if (this.territories.length === 0) {
                    // Assuming messageService is no longer available, so we'll just log
                    console.info(`No territories found for region "${regionName}"`);
                }
            },
            error: (error) => {
                // Assuming messageService is no longer available, so we'll just log
                console.error('Failed to load territories', error);
                this.loading = false;
            }
        });
    }

    onTerritoryClick(territory: TerritoryWithDetails): void {
        this.selectedTerritory = territory;
        
        // Use fitBounds to properly zoom to the territory's polygon
        if (this.mapRef?.googleMap && territory.coordinates?.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            territory.coordinates.forEach(coord => {
                bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
            });
            
            // Fit the map to the polygon bounds with padding
            this.mapRef.googleMap.fitBounds(bounds, {
                top: 50,
                right: 50,
                bottom: 50,
                left: 50
            });
        }
    }

    editTerritory(territory: TerritoryWithDetails): void {
        // Navigate to add-territory component in edit mode
        this.router.navigate(['/admin/territory-management/add'], { 
            queryParams: { 
                edit: 'true', 
                id: territory._id 
            } 
        });
    }

    private calculatePolygonCenter(coordinates: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral {
        let lat = 0, lng = 0;
        coordinates.forEach(coord => {
            lat += coord.lat;
            lng += coord.lng;
        });
        return { lat: lat / coordinates.length, lng: lng / coordinates.length };
    }

    getAssignedUsersCount(territory: TerritoryWithDetails): number {
        return territory.assignedUsersDetails?.length || 0;
    }

    getStatusSeverity(status: string): string {
        return status === 'Active' ? 'success' : 'danger';
    }

    getFilteredTerritories(): TerritoryWithDetails[] {
        let filtered = this.territories;

        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            filtered = filtered.filter(t => 
                t.name.toLowerCase().includes(query) || 
                t.region.toLowerCase().includes(query)
            );
        }

        this.products = filtered;
        return filtered;
    }

    onSearchChange(): void {
        // Explicitly update products array for mobile carousel when search changes
        const wasSearching = !!this.previousSearchQuery;
        this.getFilteredTerritories();
        
        // Restart carousel when search is cleared (backspaced fully)
        if (wasSearching && !this.searchQuery) {
            // Reset carousel to initial state by temporarily hiding and showing it
            // This forces Angular to destroy and recreate the carousel component
            this.carouselKey = -1;
            setTimeout(() => {
                this.carouselKey = 0;
            }, 0);
        }
        
        // Update previous search query
        this.previousSearchQuery = this.searchQuery;
    }

    resetRegionSelection(): void {
        // Clear session storage
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
        
        this.selectedRegionObject = null;
        this.regionSelected = false;
        this.territories = [];
        this.products = [];
        this.searchQuery = '';
        this.selectedTerritory = null;
        
        // Reset map to default view
        this.center = { lat: 0, lng: 0 };
        this.zoom = 4;
        this.options = {
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            zoomControl: true,
            scrollwheel: false, // Disabled to handle Ctrl+Scroll manually
            disableDoubleClickZoom: true,
            maxZoom: 20,
            minZoom: 3,
        };
        
        // Load the first region by default
        this.loadLastSelectedRegion();
    }

    isFSRUser(): boolean {
        const currentUser = this.authService.getLoggedInUser();
        const userRole = currentUser?.role?.identification || currentUser?.roleId?.identification;
        return userRole === 'FSR';
    }

    /**
     * Extract territory IDs from user's territories array
     * Handles both object format {_id, name, region} and string format (just the ID)
     */
    private extractTerritoryIds(userTerritories: any[]): string[] {
        return userTerritories.map(territory => {
            if (typeof territory === 'string') {
                return territory;
            } else if (territory && typeof territory === 'object' && territory._id) {
                return territory._id;
            }
            return null;
        }).filter(id => id !== null);
    }

    /**
     * Check if a territory is assigned to the current FSR user
     */
    isTerritoryAssignedToCurrentUser(territoryId: string): boolean {
        const currentUser = this.authService.getLoggedInUser();
        const userRole = currentUser?.role?.identification || currentUser?.roleId?.identification;
        
        if (userRole !== 'FSR') {
            return true; // Non-FSR users can see all territories
        }
        
        const userTerritories = currentUser?.territories || [];
        const userTerritoryIds = this.extractTerritoryIds(userTerritories);
        return userTerritoryIds.includes(territoryId);
    }

    async refreshUserAndReload(): Promise<void> {
        console.log('Manually refreshing user data and reloading territories...');
        await this.refreshCurrentUserData();
        this.loadTerritoriesByRegion();
    }

    /**
     * Check if the current user can edit territories
     */
    canEditTerritory(): boolean {
        return this.permissionService.hasPermission('territory.update');
    }

    /**
     * Check if the current user can create territories
     */
    canCreateTerritory(): boolean {
        return this.permissionService.hasPermission('territory.create');
    }

    private setupWheelZoomListener(): void {
        if (!this.mapContainer?.nativeElement || !this.mapRef?.googleMap) {
            // Retry if map container isn't ready yet
            setTimeout(() => this.setupWheelZoomListener(), 100);
            return;
        }

        const mapContainerElement = this.mapContainer.nativeElement;
        // Find parent scrollable container
        this.parentScrollable = mapContainerElement.parentElement;
        while (this.parentScrollable && !this.parentScrollable.classList.contains('overflow-scroll')) {
            this.parentScrollable = this.parentScrollable.parentElement;
        }

        // Handle wheel events - disable page scroll when over map, enable zoom with Ctrl/Cmd
        const wheelHandler = (event: WheelEvent) => {
            // Check if the event is within the map container using multiple methods
            const target = event.target as HTMLElement;
            const rect = mapContainerElement.getBoundingClientRect();
            const isOverMapByCoordinates = 
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom;

            // Also check if target is within map container (handles nested elements)
            const isOverMapByElement = mapContainerElement.contains(target);

            // Use composedPath for better detection through shadow DOM
            const isOverMapByPath = event.composedPath().some((node) => {
                return node === mapContainerElement || 
                       (node instanceof Node && mapContainerElement.contains(node));
            });

            const isOverMapElement = isOverMapByCoordinates || isOverMapByElement || isOverMapByPath;
            
            // If cursor is over map area, prevent all scrolling (page and parent container)
            if (isOverMapElement) {
                // Prevent page scrolling and parent container scrolling when over map
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                
                // If Ctrl/Cmd is pressed, handle map zoom
                if (event.ctrlKey || event.metaKey) {
                    // Calculate zoom delta based on wheel direction
                    const zoomDelta = event.deltaY > 0 ? -1 : 1;
                    const newZoom = Math.max(
                        this.options.minZoom || 3,
                        Math.min(
                            this.options.maxZoom || 20,
                            this.zoom + zoomDelta
                        )
                    );

                    if (newZoom !== this.zoom) {
                        this.ngZone.run(() => {
                            this.zoom = newZoom;
                        });
                    }
                }
                // If Ctrl/Cmd is not pressed, just prevent scrolling (map won't zoom)
            }
            // If cursor is not over map, allow normal page scrolling
        };

        // Attach wheel listener to document to catch all wheel events
        // Use capture phase to intercept before browser handles it
        document.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

        // Also attach to the map container itself as a backup
        mapContainerElement.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

        // Also attach to parent scrollable container if it exists
        if (this.parentScrollable) {
            this.parentScrollable.addEventListener('wheel', wheelHandler, { passive: false, capture: true });
        }

        // Handle mouse enter/leave to disable/enable parent scrolling
        const mouseEnterHandler = () => {
            if (this.parentScrollable) {
                this.parentScrollable.style.overflow = 'hidden';
            }
        };

        const mouseLeaveHandler = () => {
            if (this.parentScrollable) {
                this.parentScrollable.style.overflow = 'scroll';
            }
        };

        mapContainerElement.addEventListener('mouseenter', mouseEnterHandler);
        mapContainerElement.addEventListener('mouseleave', mouseLeaveHandler);

        // Store cleanup function
        this.wheelListener = () => {
            document.removeEventListener('wheel', wheelHandler, { capture: true } as EventListenerOptions);
            mapContainerElement.removeEventListener('wheel', wheelHandler, { capture: true } as EventListenerOptions);
            if (this.parentScrollable) {
                this.parentScrollable.removeEventListener('wheel', wheelHandler, { capture: true } as EventListenerOptions);
                this.parentScrollable.style.overflow = 'scroll';
            }
        };

        this.mouseEnterListener = () => {
            mapContainerElement.removeEventListener('mouseenter', mouseEnterHandler);
        };
        this.mouseLeaveListener = () => {
            mapContainerElement.removeEventListener('mouseleave', mouseLeaveHandler);
        };
    }

    private removeMouseListeners(): void {
        if (this.mouseEnterListener) {
            this.mouseEnterListener();
            this.mouseEnterListener = undefined;
        }
        if (this.mouseLeaveListener) {
            this.mouseLeaveListener();
            this.mouseLeaveListener = undefined;
        }
        if (this.parentScrollable) {
            this.parentScrollable.style.overflow = 'scroll';
        }
    }

    private removeWheelListener(): void {
        if (this.wheelListener) {
            this.wheelListener();
            this.wheelListener = undefined;
        }
    }
} 